import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import {
  StickyHeaderNavigation,
  FixedBottomTwoButtons,
  FixedBottomButton,
} from '@/components'
import { RequestHeader, RequestNote } from '@/components/RequestDetails'
import { Request } from '@/domain/requests'
import { useRequestItemHandler } from '@/hooks/useRequestItemHandler'
import { useTheme } from '@/hooks/useTheme'
import useOffsets from '@/hooks/useOffsets'

export const RequestDetailsScreen: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const theme = useTheme()
  const offsets = useOffsets()

  const handleBack = () => {
    navigate(-1)
  }

  // Get request data from navigation state (like in RN)
  const request: Request | null = location.state?.request || null

  // Handle request actions (only when request is loaded)
  const {
    formattedRequest,
    handleCancel,
    handleNext,
    isLoadingCancel,
    isLoadingNext,
  } = useRequestItemHandler({
    request: request!,
    onCancelSuccess: () => navigate(-1),
    byDetailsScreen: true,
    enabled: !!request, // Only enable when request is available
  })

  // Error state - no request data
  if (!request) {
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          minHeight: '100vh',
          backgroundColor: 'var(--color-background-screen)',
        }}
      >
        <StickyHeaderNavigation
          title={t('screens:requestDetails', 'Request Details')}
          showBackButton={true}
          onBackPress={handleBack}
        />

        <BaseView
          flex={1}
          center
          gap={16}
          style={{
            paddingTop: '70px',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingBottom: '12px',
          }}
        >
          <BaseText
            fontSize={16}
            fontFamily="medium"
            color="label.error"
            textAlign="center"
          >
            {t('screens:requestNotFound', 'Request not found')}
          </BaseText>

          <BaseView
            py={12}
            px={24}
            center
            style={{
              backgroundColor: 'transparent',
              borderRadius: '12px',
              border: `1px solid ${theme.colors.background.secondaryGray}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onClick={handleBack}
          >
            <BaseText fontSize={16} fontFamily="medium" color="label.default">
              {t('form:actions.goBack', 'Go Back')}
            </BaseText>
          </BaseView>
        </BaseView>
      </div>
    )
  }

  return (
    <div
      style={{
        paddingTop: offsets.top - 30,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        minHeight: '100vh',
        backgroundColor: 'var(--color-background-screen)',
      }}
    >
      {/* Sticky Header Navigation */}
      <StickyHeaderNavigation
        title={t('screens:requestDetails', 'Request Details')}
        showBackButton={true}
        onBackPress={handleBack}
      />

      {/* Content Container */}
      <BaseView
        data-component="request-details-screen"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '70px',
          position: 'relative',
          height: '100%',
          minHeight: 'calc(100vh - 70px)',
          width: '100%',
        }}
      >
        {/* Scrollable Content */}
        <BaseView
          style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: '100px', // Space for fixed buttons
          }}
        >
          {/* Request Header with Avatar and Amount */}
          {formattedRequest && (
            <RequestHeader
              avatar={formattedRequest.avatar}
              title={formattedRequest.title}
              userFullName={formattedRequest.userFullName}
              amount={request.amountFormatted}
              fiatAmount={request.relativeFiatAmountFormatted}
            />
          )}

          {/* Request Note */}
          <BaseView mt={40}>
            <RequestNote note={request.note} />
          </BaseView>
        </BaseView>

        {/* Fixed Action Buttons */}
        {formattedRequest &&
          (formattedRequest.isPaymentLink ? (
            formattedRequest.isPending ? (
              <FixedBottomButton
                buttons={{
                  label: formattedRequest.cancelLabel,
                  onPress: handleCancel,
                  isLoading: isLoadingCancel || false,
                  type: 'secondaryGray',
                }}
              />
            ) : (
              <BaseView
                center
                py={16}
                style={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  paddingBottom: offsets.bottom + 12,
                }}
              >
                <BaseText
                  fontSize={16}
                  fontFamily="medium"
                  style={{
                    color:
                      request.state === 'accepted' ? '#34C759' : '#FF453A',
                  }}
                >
                  {request.state === 'accepted'
                    ? t('translation:claimed', 'Claimed')
                    : t('translation:cancelled', 'Cancelled')}
                </BaseText>
              </BaseView>
            )
          ) : (
            handleCancel &&
            handleNext && (
              <FixedBottomTwoButtons
                leftButton={{
                  label: formattedRequest.cancelLabel,
                  onPress: handleCancel,
                  isLoading: isLoadingCancel || false,
                  type: 'secondaryGray',
                }}
                rightButton={{
                  label: formattedRequest.nextLabel,
                  onPress: handleNext,
                  isLoading: isLoadingNext || false,
                }}
              />
            )
          ))}
      </BaseView>
    </div>
  )
}
